import { splashLogo } from '@/assets/images';
import { Stack } from '@/components/layout';
import { showAlert, SplashLoadingIndicator } from '@/components/ui';
import { STRINGS } from '@/constants/strings';
import { resolveGuardianEntry } from '@/features/guardian/utils/resolve-guardian-entry';
import { createStyles, useDailyBackground, useRootNavigation, useScaling } from '@/hooks';
import { useAppFonts } from '@/libs/fonts/use-app-fonts';
import { ensureOnline } from '@/libs/network/network-status';
import {
  notifyRevoPushAppReady,
  restartRevoPushApp,
  syncRevoPushUpdate,
  SyncStatus,
} from '@/libs/revopush';
import { resolvePostAuthTarget } from '@/navigations/resolve-post-auth-target';
import { useSessionStore } from '@/stores';
import { prefetchImageLevelIcon } from '@/utils/helper';

import { useEffect, useEffectEvent, useState } from 'react';
import { Image, ImageBackground, ImageStyle, Platform } from 'react-native';
import { useShallow } from 'zustand/shallow';

export function SplashScreen() {
  const styles = useStyles();
  const isInitialized = useSessionStore(useShallow(state => state.isInitialized));
  const isAuthenticated = useSessionStore(useShallow(state => state.isAuthenticated));
  const fontsReady = useAppFonts();

  const navigation = useRootNavigation();
  const { scale } = useScaling();
  const backgroundSource = useDailyBackground();
  const resizeMode = 'cover';
  const cardWidth = { phone: 303, tablet: 300 } as const;
  const [updateProgress, setUpdateProgress] = useState<number | undefined>(undefined);
  const [updateStatus, setUpdateStatus] = useState<SyncStatus | undefined>(undefined);

  const initApp = useEffectEvent(async () => {
    //Prefetch image LV
    prefetchImageLevelIcon();
    if (!isInitialized || !fontsReady) {
      return;
    }
    await new Promise<void>(resolve => setTimeout(resolve, 2000));
    if (isAuthenticated) {
      // Soft logout persists across restarts: a killed-then-reopened app whose session is
      // still valid must land back on the biometric/password login screen, not straight in.
      if (useSessionStore.getState().isSoftLoggedOut) {
        return navigation.replace('UnAuth', { screen: 'LoginWithBiometrics' });
      }
      // Land on the flow matching the user's role(s): RoleSelect / Auth / Guardian.
      const { roles, activeRole } = useSessionStore.getState();
      const target = resolvePostAuthTarget(roles, activeRole);
      switch (target.screen) {
        case 'Guardian':
          // Gate guardians on their onboarding progress: resume the wizard at the
          // saved step, or skip straight to the dashboard once it's complete.
          return navigation.replace('Guardian', await resolveGuardianEntry());
        case 'RoleSelect':
          return navigation.replace('RoleSelect');
        default:
          return navigation.replace('Auth', target.params);
      }
    }
    return navigation.replace('UnAuth', {
      screen: 'Welcome',
    });
  });

  useEffect(() => {
    // Confirm the currently-running bundle booted successfully as early as possible.
    // This MUST NOT wait for the update sync below: on a freshly-installed bundle's first
    // launch it is in a `pending` state, and if the app is killed/restarted before this
    // call runs, RevoPush treats the (healthy) update as failed and rolls it back — marking
    // its hash failed so it never reinstalls without a full uninstall. Gating it behind the
    // network-bound sync made that window several seconds wide; here it's milliseconds.
    notifyRevoPushAppReady();
    ensureOnline();
  }, []);

  useEffect(() => {
    // If we're still stuck on the splash screen after 20s, prompt the user to
    // restart. The timer is cleared on unmount, i.e. once we navigate away.
    const timeoutId = setTimeout(() => {
      showAlert({
        title: STRINGS.splash.loadTimeout.title,
        message: STRINGS.splash.loadTimeout.message,
        buttons: [
          {
            text: STRINGS.splash.loadTimeout.retry,
            onPress: () => restartRevoPushApp(),
          },
        ],
      });
    }, 180000);

    return () => clearTimeout(timeoutId);
  }, []);

  useEffect(() => {
    if (updateStatus === SyncStatus.UP_TO_DATE || updateStatus === SyncStatus.UPDATE_INSTALLED) {
      initApp();
    }
  }, [navigation, isInitialized, isAuthenticated, fontsReady, updateStatus]);

  useEffect(() => {
    (async () => {
      // Non-mandatory updates: download in-session but defer activation to next cold start.
      // Mandatory updates: download + install without auto-restart; user confirms via modal.
      const result = await syncRevoPushUpdate({
        onDownloadProgress: p => {
          setUpdateProgress(p.receivedBytes / p.totalBytes);
        },
        immediateInstall: false,
        deferMandatoryRestart: true,
        onStatusChange: status => {
          switch (status) {
            case SyncStatus.CHECKING_FOR_UPDATE:
              break;
            case SyncStatus.DOWNLOADING_PACKAGE:
              break;
            case SyncStatus.INSTALLING_UPDATE:
              break;
            case SyncStatus.UP_TO_DATE:
              setUpdateStatus(SyncStatus.UP_TO_DATE);
              setUpdateProgress(undefined);
              break;
            case SyncStatus.UPDATE_INSTALLED:
              setUpdateProgress(undefined);
              break;
          }
        },
      });

      if (result.status === 'update_installed' && result.isMandatory) {
        showAlert({
          title: STRINGS.splash.mandatoryRestart.title,
          message: STRINGS.splash.mandatoryRestart.message,
          buttons: [
            {
              text: STRINGS.splash.mandatoryRestart.restartNow,
              onPress: () => restartRevoPushApp(),
            },
          ],
        });
        return;
      }

      if (result.status === 'update_installed') {
        setUpdateStatus(SyncStatus.UPDATE_INSTALLED);
      }

      if (result.status === 'unsupported_platform' && Platform.OS === 'web') {
        setUpdateStatus(SyncStatus.UP_TO_DATE);
        setUpdateProgress(undefined);
      }
    })();
  }, []);

  return (
    <Stack flex={1}>
      <ImageBackground
        source={backgroundSource}
        style={styles.background}
        imageStyle={Platform.OS === 'web' && styles.webBackgroundFill}
        resizeMode={resizeMode}>
        <Stack
          position="absolute"
          width={scale(cardWidth)}
          height={scale({
            phone: 395,
            tablet: 240,
          })}
          paddingTop={scale({
            phone: 72,
            tablet: 0,
          })}
          backgroundColor="#fff"
          align="center"
          justify="space-between">
          <Image source={splashLogo} style={styles.logo} resizeMode="contain" />
          <SplashLoadingIndicator progress={updateProgress} />
        </Stack>
      </ImageBackground>
    </Stack>
  );
}

const useStyles = createStyles(({ scale }) => ({
  background: {
    flex: 1,
    width: '100%',
    height: '100%',
    alignItems: 'center',
    justifyContent: 'center',
  },
  logo: {
    width: scale(220),
    height: scale(220),
  },
  webBackgroundFill: {
    width: '100%',
    height: '100vh',
  } as unknown as ImageStyle,
}));
